import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Users, Target, Shield } from 'lucide-react';

const EnhancedStatsTable = ({ players = [], season = null, title = 'Player Statistics' }) => {
  const [activeTab, setActiveTab] = useState('all');

  // Process players data for enhanced display
  const processedPlayers = useMemo(() => {
    // If players are already processed, use them directly
    if (players.length > 0 && players[0].processedStats) {
      return players.map((player) => ({
        ...player,
        positions: player.processedStats.positions || [],
        statsByPosition: player.processedStats.statsByPosition || {},
        primaryPosition: player.processedStats.primaryPosition || 'forward',
        totalGames: player.processedStats.totalGames || 0,
        isDualPosition: player.processedStats.isDualPosition || false,
        displayName: player.name || 'Unknown Player',
      }));
    }

    // Legacy processing for non-processed players
    return players.map((player) => {
      const positions = [];
      const statsByPosition = {};
      let totalGames = 0;
      let primaryPosition = 'forward';

      // Determine player's positions and aggregate stats
      if (player.seasonStats && Array.isArray(player.seasonStats)) {
        const relevantStats = season
          ? player.seasonStats.filter((stat) => stat.season?._id === season._id)
          : player.seasonStats;

        relevantStats.forEach((stat) => {
          const position =
            stat.statsType === 'goalie'
              ? 'goalie'
              : stat.statsType === 'skater'
                ? 'skater'
                : stat.statsType || 'skater';

          if (!positions.includes(position)) {
            positions.push(position);
          }

          statsByPosition[position] = stat;
          totalGames += stat.gp || 0;
        });
      }

      // Fallback to current season stats
      if (positions.length === 0 && player.currentSeasonStats) {
        const isGoalie =
          player.positionGroup?.toLowerCase() === 'goalie' ||
          player.currentSeasonStats.sa > 0 ||
          player.currentSeasonStats.ga >= 0;
        const position = isGoalie ? 'goalie' : 'skater';
        positions.push(position);
        statsByPosition[position] = player.currentSeasonStats;
        totalGames = player.currentSeasonStats.gp || 0;
      }

      // Determine primary position
      if (positions.includes('goalie') && positions.includes('skater')) {
        // Dual position - determine primary based on games played
        const goalieGames = statsByPosition.goalie?.gp || 0;
        const skaterGames = statsByPosition.skater?.gp || 0;
        primaryPosition = goalieGames > skaterGames ? 'goalie' : 'skater';
      } else if (positions.includes('goalie')) {
        primaryPosition = 'goalie';
      } else {
        primaryPosition = 'skater';
      }

      return {
        ...player,
        positions,
        statsByPosition,
        primaryPosition,
        totalGames,
        isDualPosition: positions.length > 1,
        displayName: player.name || 'Unknown Player',
      };
    });
  }, [players, season]);

  // Filter players based on active tab with strict separation
  const filteredPlayers = useMemo(() => {
    if (!processedPlayers || processedPlayers.length === 0) {
      return [];
    }

    switch (activeTab) {
      case 'forwards':
        return processedPlayers.filter((p) => p.positionGroup?.toLowerCase() === 'forward');
      case 'defense':
        return processedPlayers.filter((p) => p.positionGroup?.toLowerCase() === 'defense');
      case 'goalies':
        return processedPlayers.filter((p) => {
          // Always show if primary position is goalie
          if (p.positionGroup?.toLowerCase() === 'goalie') {
            return true;
          }

          // For non-primary goalies, only show if they played goalie games in the selected season
          if (p.seasonStats && Array.isArray(p.seasonStats)) {
            const relevantSeasonStats = season
              ? p.seasonStats.filter((stat) => stat.season?._id === season._id)
              : p.seasonStats;

            const hasGoalieGamesInSeason = relevantSeasonStats.some(
              (stat) => stat.statsType === 'goalie' && (stat.gp || 0) > 0
            );
            if (hasGoalieGamesInSeason) return true;
          }

          // Check current season stats for goalie activity (only when no specific season is selected)
          if (!season && p.currentSeasonStats) {
            const hasGoalieActivity =
              (p.currentSeasonStats.sa || 0) > 0 ||
              (p.currentSeasonStats.ga || 0) > 0 ||
              (p.currentSeasonStats.sv || 0) > 0;
            if (hasGoalieActivity) return true;
          }

          return false;
        });
      default:
        // All players tab - exclude goalies, show only skaters
        return processedPlayers.filter((p) => p.positionGroup?.toLowerCase() !== 'goalie');
    }
  }, [processedPlayers, activeTab, season]);

  // Sort filtered players by appropriate stats
  const sortedPlayers = useMemo(() => {
    if (!filteredPlayers || filteredPlayers.length === 0) {
      return [];
    }

    return [...filteredPlayers].sort((a, b) => {
      // Get stats for both players
      let aStats = null;
      let bStats = null;

      // Use the same stats selection logic as in the render function
      const getPlayerStats = (player) => {
        let stats = null;

        if (player.processedStats?.statsByPosition) {
          if (activeTab === 'goalies' || player.positionGroup?.toLowerCase() === 'goalie') {
            stats = player.processedStats.statsByPosition.goalie;
          } else {
            stats =
              player.processedStats.statsByPosition.skater ||
              player.processedStats.statsByPosition.forward;
          }
        }

        if (!stats && player.seasonStats?.length > 0) {
          const relevantStats = season
            ? player.seasonStats.filter((stat) => stat.season?._id === season._id)
            : player.seasonStats;

          if (activeTab === 'goalies' || player.positionGroup?.toLowerCase() === 'goalie') {
            stats = relevantStats.find((stat) => stat.statsType === 'goalie');
          } else {
            stats =
              relevantStats.find(
                (stat) =>
                  stat.statsType === 'skater' ||
                  stat.statsType === 'forward' ||
                  stat.statsType === 'defense'
              ) || relevantStats[0];
          }
        }

        if (!stats && player.currentSeasonStats) {
          stats = player.currentSeasonStats;
        }

        return stats || {};
      };

      aStats = getPlayerStats(a);
      bStats = getPlayerStats(b);

      // Sort by different metrics based on tab
      if (activeTab === 'goalies') {
        // For goalies, sort by saves (descending), then by save percentage
        const aSaves = aStats.sv || 0;
        const bSaves = bStats.sv || 0;
        if (aSaves !== bSaves) {
          return bSaves - aSaves; // Higher saves first
        }
        // If saves are equal, sort by save percentage
        const aSvp = aStats.svp || 0;
        const bSvp = bStats.svp || 0;
        return bSvp - aSvp; // Higher save percentage first
      } else {
        // For skaters, sort by points (descending)
        const aPoints = aStats.pts || (aStats.g || 0) + (aStats.a || 0);
        const bPoints = bStats.pts || (bStats.g || 0) + (bStats.a || 0);
        if (aPoints !== bPoints) {
          return bPoints - aPoints; // Higher points first
        }
        // If points are equal, sort by goals
        const aGoals = aStats.g || 0;
        const bGoals = bStats.g || 0;
        return bGoals - aGoals; // Higher goals first
      }
    });
  }, [filteredPlayers, activeTab, season]);

  const renderStatsRow = (player, stats) => {
    const isGoalie = player.positionGroup?.toLowerCase() === 'goalie';
    const isInactive = player.active === false;
    const rowClasses = `transition-colors ${
      isInactive
        ? 'bg-gray-800/50 hover:bg-gray-700/50 opacity-75'
        : 'bg-gray-800 hover:bg-gray-700'
    }`;

    return (
      <tr key={player._id} className={rowClasses}>
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="flex items-center space-x-2">
            {isInactive && (
              <span className="bg-red-600 text-white text-xs font-bold px-2 py-1 rounded">
                INACTIVE
              </span>
            )}
            {player.slug?.current ? (
              <Link
                to={`/players/${player.slug.current}`}
                className={`font-medium transition-colors ${
                  isInactive
                    ? 'text-gray-400 hover:text-gray-300'
                    : 'text-white hover:text-yellow-400'
                }`}
              >
                {player.displayName}
              </Link>
            ) : (
              <span className={`font-medium ${isInactive ? 'text-gray-400' : 'text-white'}`}>
                {player.displayName}
              </span>
            )}
          </div>
        </td>

        <td className="px-6 py-4 whitespace-nowrap text-sm text-white">#{player.number || '—'}</td>

        {activeTab !== 'goalies' && (
          <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
            {player.positionGroup || '—'}
          </td>
        )}

        <td className="px-6 py-4 whitespace-nowrap text-sm text-white">{stats?.gp || 0}</td>

        {activeTab === 'goalies' ? (
          // Goalie stats
          <>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-white">{stats?.sa || 0}</td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-white">{stats?.ga || 0}</td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-white">{stats?.sv || 0}</td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
              {stats?.svp ? `${stats.svp}%` : '0%'}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
              {stats?.gaa || '0.00'}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-white" colSpan="5">
              —
            </td>
          </>
        ) : (
          // Skater stats
          <>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-white">{stats?.g || 0}</td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-white">{stats?.a || 0}</td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
              {stats?.pts || (stats?.g || 0) + (stats?.a || 0)}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-white">{stats?.pim || 0}</td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-white">{stats?.ppg || 0}</td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-white">{stats?.ppa || 0}</td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-white">{stats?.shg || 0}</td>
          </>
        )}

        <td className="px-6 py-4 whitespace-nowrap text-sm text-white">{stats?.mins || 0}</td>
      </tr>
    );
  };

  const tabs = [
    { key: 'all', label: 'All Players', icon: Users },
    { key: 'forwards', label: 'Forwards', icon: Users },
    { key: 'defense', label: 'Defense', icon: Target },
    { key: 'goalies', label: 'Goalies', icon: Shield },
  ];

  return (
    <div className="bg-gray-900 shadow-lg rounded-lg overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-700">
        <h3 className="text-lg font-semibold text-white">{title}</h3>
        {season && <p className="text-sm text-gray-300 mt-1">{season.name} Season Statistics</p>}
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-700">
        <nav className="flex space-x-8 px-6" aria-label="Tabs">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`${
                  isActive
                    ? 'border-yellow-500 text-yellow-400'
                    : 'border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-500'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors`}
              >
                <Icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-700">
          <thead className="bg-black">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-yellow-500 uppercase tracking-wider">
                Player
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-yellow-500 uppercase tracking-wider">
                #
              </th>
              {activeTab !== 'goalies' && (
                <th className="px-6 py-3 text-left text-xs font-medium text-yellow-500 uppercase tracking-wider">
                  Position
                </th>
              )}
              <th className="px-6 py-3 text-left text-xs font-medium text-yellow-500 uppercase tracking-wider">
                GP
              </th>
              {activeTab === 'goalies' ? (
                <>
                  <th className="px-6 py-3 text-left text-xs font-medium text-yellow-500 uppercase tracking-wider">
                    SA
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-yellow-500 uppercase tracking-wider">
                    GA
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-yellow-500 uppercase tracking-wider">
                    SV
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-yellow-500 uppercase tracking-wider">
                    SV%
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-yellow-500 uppercase tracking-wider">
                    GAA
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-yellow-500 uppercase tracking-wider"
                    colSpan="5"
                  >
                    —
                  </th>
                </>
              ) : (
                <>
                  <th className="px-6 py-3 text-left text-xs font-medium text-yellow-500 uppercase tracking-wider">
                    G
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-yellow-500 uppercase tracking-wider">
                    A
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-yellow-500 uppercase tracking-wider">
                    PTS
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-yellow-500 uppercase tracking-wider">
                    PIM
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-yellow-500 uppercase tracking-wider">
                    PPG
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-yellow-500 uppercase tracking-wider">
                    PPA
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-yellow-500 uppercase tracking-wider">
                    SHG
                  </th>
                </>
              )}
              <th className="px-6 py-3 text-left text-xs font-medium text-yellow-500 uppercase tracking-wider">
                MIN
              </th>
            </tr>
          </thead>
          <tbody className="bg-gray-800 divide-y divide-gray-700">
            {sortedPlayers.length === 0 ? (
              <tr>
                <td colSpan="12" className="px-6 py-8 text-center text-gray-400">
                  No players found for this position.
                </td>
              </tr>
            ) : (
              sortedPlayers.map((player) => {
                // Select appropriate stats based on player's position and tab
                let stats = null;

                // Check if player has processed stats from the processing function
                if (player.processedStats?.statsByPosition) {
                  if (activeTab === 'goalies' || player.positionGroup?.toLowerCase() === 'goalie') {
                    stats = player.processedStats.statsByPosition.goalie;
                  } else {
                    stats =
                      player.processedStats.statsByPosition.skater ||
                      player.processedStats.statsByPosition.forward;
                  }
                }

                // Fallback to seasonStats array if no processed stats
                if (!stats && player.seasonStats?.length > 0) {
                  // Find the most recent season stats for the appropriate type
                  const relevantStats = season
                    ? player.seasonStats.filter((stat) => stat.season?._id === season._id)
                    : player.seasonStats;

                  if (activeTab === 'goalies' || player.positionGroup?.toLowerCase() === 'goalie') {
                    stats = relevantStats.find((stat) => stat.statsType === 'goalie');
                  } else {
                    // For forwards and defense, look for skater or position-specific stats
                    stats =
                      relevantStats.find(
                        (stat) =>
                          stat.statsType === 'skater' ||
                          stat.statsType === 'forward' ||
                          stat.statsType === 'defense'
                      ) || relevantStats[0]; // fallback to first available
                  }
                }

                // Final fallback to currentSeasonStats if it exists
                if (!stats && player.currentSeasonStats) {
                  stats = player.currentSeasonStats;
                }

                return renderStatsRow(player, stats || {});
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EnhancedStatsTable;
